import type {
  PayeeAdjustmentsAggregate,
  PayeeTransactionsSummaryAggregate,
} from "common/graphql/queries/GetPayeeTransactionsOverview";

/**
 * The CurbSuite overview's period totals, all signed from the payee's side: positive is money
 * charged, negative is a net reversal (more charged back in the period than charged).
 */
export interface AdjustedOverviewSums {
  potentialRevenueAmount: number;
  /** Park total, net of park-referenced adjustments. */
  registeredTransactionsAmount: number;
  /** Gateway (wallet) total, net of wallet_reload- and permit-referenced adjustments. */
  walletTransactionsAmount: number;
  tripChargeAmount: number;
}

/**
 * The CurbSuite overview's period sums with adjustments (chargebacks/credits, AUT-6163) folded into
 * the tab of the charge they adjust. Each adjustment counts as its own transaction, in the period of
 * its own date, so a chargeback lowers the total of the month it was made in.
 *
 * The daily summary's buckets key on transaction_type and so hold no adjustments; their sums come
 * from the log view's `price` (the single source of truth for price), split by reference:
 * `parkAdjustments` (reference_transaction_type = park) and `gatewayAdjustments`
 * (reference_gateway_transaction_type in wallet_reload, permit).
 *
 * Both the summary and `price` are payer-wallet effects (AUT-6160): park and trip-charge rows are
 * negative, gateway rows positive, and an adjustment's `price` is its own wallet effect. Each bucket
 * is netted in that convention and then flipped to the payee's side once, here, so the caller never
 * needs `Math.abs` -- which would show a net reversal as a positive total.
 */
export function getAdjustedOverviewSums(
  summary: PayeeTransactionsSummaryAggregate | undefined,
  parkAdjustments: PayeeAdjustmentsAggregate | undefined,
  gatewayAdjustments: PayeeAdjustmentsAggregate | undefined,
): AdjustedOverviewSums {
  const sum = summary?.aggregate.sum;
  const parkWalletEffect =
    (Number(sum?.registeredTransactionsAmount) || 0) + (Number(parkAdjustments?.aggregate.sum.price) || 0);
  const gatewayWalletEffect =
    (Number(sum?.walletTransactionsAmount) || 0) + (Number(gatewayAdjustments?.aggregate.sum.price) || 0);
  return {
    potentialRevenueAmount: Number(sum?.potentialRevenueAmount) || 0,
    // `|| 0` turns -0 (no parks) into 0.
    registeredTransactionsAmount: -parkWalletEffect || 0,
    walletTransactionsAmount: gatewayWalletEffect,
    // Nothing can reference a trip charge, so it has no adjustments.
    tripChargeAmount: -(Number(sum?.tripChargeAmount) || 0) || 0,
  };
}

/**
 * The overview card's total for a tab: one bucket, or both for bus stations, whose park tab also
 * shows permit reconciliation rows. Signs are kept (see getAdjustedOverviewSums).
 */
export function getOverviewAmount(
  sums: AdjustedOverviewSums,
  key: keyof AdjustedOverviewSums,
  { isBusStation = false }: { isBusStation?: boolean } = {},
): number {
  return isBusStation ? sums.registeredTransactionsAmount + sums.walletTransactionsAmount : sums[key];
}
